import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Package, Factory, Truck, Home, Search, ExternalLink, PawPrint } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { WaveDivider } from "@/components/decor";
import { formatAUD } from "@/lib/media";

const STEPS = [
  { key: "placed", label: "Order placed", Icon: Package, blurb: "Paid & confirmed" },
  { key: "production", label: "In production", Icon: Factory, blurb: "Being made with love" },
  { key: "shipped", label: "Shipped", Icon: Truck, blurb: "On its way to you" },
  { key: "delivered", label: "Delivered", Icon: Home, blurb: "Home at last" },
];
export const stepIndex = (o) => {
  const s = (o?.fulfillment_status || "").toLowerCase();
  if (s === "delivered" || o?.delivered_at) return 3;
  if (["shipped", "in_transit", "fulfilled", "partial"].includes(s) || o?.shipped_at) return 2;
  if (["inprocess", "in_production", "pending", "draft", "printed", "printing"].includes(s)) return 1;
  return 0;
};

function PawPath({ active }) {
  const pct = [6, 36, 66, 94];
  return (
    <div className="relative mt-8 mb-4 h-40 sm:h-36" data-testid="track-progress">
      <svg viewBox="0 0 1000 140" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <path d="M30 100 C 200 20, 300 20, 420 90 S 640 150, 760 60 S 900 20, 970 70" fill="none" stroke="#19324A" strokeOpacity="0.12" strokeWidth="6" strokeDasharray="2 14" strokeLinecap="round" />
        <path d="M30 100 C 200 20, 300 20, 420 90 S 640 150, 760 60 S 900 20, 970 70" fill="none" stroke="#FF6B9D" strokeWidth="6" strokeLinecap="round"
          pathLength="100" strokeDasharray={`${pct[active]} 100`} style={{ transition: "stroke-dasharray 1.2s cubic-bezier(.2,.8,.2,1)" }} />
      </svg>
      {STEPS.map((st, i) => {
        const done = i <= active;
        const ys = [100, 32, 110, 70];
        return (
          <div key={st.key} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-center w-24" style={{ left: `${pct[i]}%`, top: `${(ys[i] / 140) * 100}%` }} data-testid={`track-step-${st.key}`} data-active={done}>
            <div className={`w-11 h-11 rounded-full flex items-center justify-center soft-shadow transition-all duration-500 ${done ? "bg-[#FFD93D] text-[#19324A] scale-110" : "bg-white text-[#19324A]/35"} ${i === active ? "ring-4 ring-[#FF6B9D]/40 animate-pulse" : ""}`}>
              <st.Icon className="w-5 h-5" />
            </div>
            <span className={`mt-1 text-[11px] sm:text-xs font-display font-bold leading-tight ${done ? "text-[#19324A]" : "text-[#19324A]/40"}`}>{st.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function TrackOrder() {
  useSeo("Where's my order? — Track your Happy Bulldog Co. parcel", "Follow your Chewie & Solo order from our den to your door.");
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [num, setNum] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || user?.email || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!num.trim()) return;
    setLoading(true); setError(""); setOrder(null);
    try { const { data } = await api.get(`/orders/track/${encodeURIComponent(num.trim())}`, { params: email ? { email: email.trim() } : {} }); setOrder(data); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (params.get("order")) lookup(); }, []);

  const active = order ? stepIndex(order) : 0;
  const track = order?.tracking?.find((t) => t.tracking_url || t.tracking_number);
  const failed = ["failed", "canceled", "on_hold", "returned", "missing_address"].includes((order?.fulfillment_status || "").toLowerCase());

  return (
    <div>
      <section className="relative bg-[#4CC9F0]/20 py-14 text-center overflow-hidden">
        <div className="relative max-w-2xl mx-auto px-4">
          <span className="font-hand text-2xl text-[#FF6B9D]">Sniffing out your parcel</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-1">WHERE'S MY ORDER?</h1>
          <p className="mt-3 text-[#19324A]/70">Pop in your order number and the email you ordered with.</p>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-2xl mx-auto px-4 -mt-4 pb-20">
        <form onSubmit={lookup} className="bg-white rounded-3xl soft-shadow p-5 sm:p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="track-form">
          <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Order number (e.g. HBC-AB12CD)" data-testid="track-order-input"
            className="rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 font-display font-bold uppercase outline-none focus:border-[#FFD93D]" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email you ordered with" data-testid="track-email-input"
            className="rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D]" />
          <button className="btn-pink" disabled={loading} data-testid="track-submit"><Search className="w-4 h-4" />{loading ? "Sniffing…" : "Track"}</button>
        </form>
        {error && <p className="mt-4 text-center text-sm font-semibold text-[#FF6B9D]" data-testid="track-error">{error}</p>}

        {order && (
          <div className="mt-6 bg-white rounded-3xl soft-shadow p-5 sm:p-8" data-testid="track-result">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs font-bold text-[#19324A]/50 uppercase tracking-wide">Order</p>
                <p className="font-display font-extrabold text-2xl text-[#19324A]" data-testid="track-order-number">{order.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-[#19324A]/50 uppercase tracking-wide">Status</p>
                <p className="font-display font-bold text-[#19324A]" data-testid="track-status-label">{order.pickup ? (order.pickup_status === "collected" ? "Collected" : order.pickup_status === "ready" ? "Ready to collect" : "Being packed") : failed ? "Needs attention" : STEPS[active].label}</p>
              </div>
            </div>

            {order.pickup ? (
              <div className="mt-6 rounded-2xl bg-[#7ED957]/15 border border-[#7ED957]/50 p-4 text-sm text-[#19324A]" data-testid="track-pickup">
                <p className="font-bold text-[#166534]">🐾 Local pickup — Slacks Creek QLD (no postage)</p>
                {order.pickup_status === "collected" ? <p className="mt-1">Collected{order.picked_up_at ? ` on ${new Date(order.picked_up_at).toLocaleDateString("en-AU")}` : ""} — thanks for visiting the den!</p>
                  : order.pickup_status === "ready" ? <><p className="mt-1">Your order is packed and ready. {order.pickup_note ? "Collection details:" : "Check your inbox for collection details."}</p>{order.pickup_note && <p className="mt-1 whitespace-pre-line font-semibold" data-testid="track-pickup-note">{order.pickup_note}</p>}</>
                  : <p className="mt-1">We're packing it now and will email you the moment it's ready to collect.</p>}
              </div>
            ) : failed ? (
              <div className="mt-6 rounded-2xl bg-[#FF6B9D]/10 border border-[#FF6B9D]/30 p-4 text-sm text-[#19324A]" data-testid="track-failed">
                There's a small hold-up on this order ({order.fulfillment_status.replace(/_/g, " ")}). We're already on it — <Link to="/contact" className="font-bold text-[#FF6B9D] underline">give us a shout</Link> if you'd like an update.
              </div>
            ) : (
              <>
                <PawPath active={active} />
                <div className="flex items-center justify-between text-xs text-[#19324A]/55 font-semibold px-1">
                  <span className="inline-flex items-center gap-1"><PawPrint className="w-3.5 h-3.5 text-[#FFD93D]" /> Our den</span>
                  <span className="text-center">{STEPS[active].blurb}</span>
                  <span className="inline-flex items-center gap-1">{order.destination?.city ? `${order.destination.city}${order.destination.state_code ? `, ${order.destination.state_code}` : ""}` : "Your door"} <Home className="w-3.5 h-3.5 text-[#FF6B9D]" /></span>
                </div>
              </>
            )}

            {track && (
              <div className="mt-6 rounded-2xl bg-[#FFF9EE] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="track-carrier">
                <div>
                  <p className="text-xs font-bold text-[#19324A]/50 uppercase tracking-wide">Carrier</p>
                  <p className="font-display font-bold text-[#19324A]">{[track.carrier, track.service].filter(Boolean).join(" · ") || "Courier"}{track.tracking_number ? ` — ${track.tracking_number}` : ""}</p>
                </div>
                {track.tracking_url && <a href={track.tracking_url} target="_blank" rel="noreferrer" className="btn-outline !py-2" data-testid="track-carrier-link"><ExternalLink className="w-4 h-4" /> Track with carrier</a>}
              </div>
            )}

            <div className="mt-6 border-t border-[#19324A]/10 pt-4">
              <p className="text-xs font-bold text-[#19324A]/50 uppercase tracking-wide mb-2">In the box</p>
              <ul className="space-y-1 text-sm text-[#19324A]" data-testid="track-items">
                {(order.items || []).map((it, i) => <li key={i} className="flex justify-between"><span>{it.name}{it.size || it.colour ? ` (${[it.size, it.colour].filter(Boolean).join(" / ")})` : ""} × {it.quantity}</span><span className="text-[#19324A]/60">{formatAUD(it.price * it.quantity)}</span></li>)}
              </ul>
              <p className="mt-3 text-xs text-[#19324A]/50">Placed {new Date(order.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}{order.shipped_at ? ` · Shipped ${new Date(order.shipped_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : ""}</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
